"use client";

import { IUserSelectable } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import React, { useMemo } from "react";
import { UserAvatar } from "../../data-display/UserAvatar/UserAvatar";
import { UserLabel } from "../../labels/UserLabel/UserLabel";
import { FormControlWrapper } from "../FormControlWrapper";
import { FormControlOutline } from "../FormControlWrapper/FormControlOutline";
import { splitFormControlWrapperProps } from "../FormControlWrapper/helpers";
import { SelectItem, UnifiedSelect } from "../UnifiedSelect";
import {
  UserSelectInputProps,
  UserSelectInputPropsWithFormControl
} from "./UserSelectInput.types";

/** Sentinel id for the "Clear selection" entry; not a real user id. */
const CLEAR_ITEM_ID = "__user-select-clear__";

const UserSelectInputInner: React.FC<UserSelectInputProps> = (props) => {
  const {
    users,
    value,
    onChangeValue,
    values,
    onChangeValues,
    multiselect = false,
    inputClassName,
    placeholder = "Select user...",
    disabled,
    isLoading,
    clearable = false,
    clearLabel = "Clear selection",
    ...rest
  } = props;

  // Convert users to SelectItem format
  const items = useMemo<SelectItem<IUserSelectable>[]>(() => {
    const userItems = users.map((user) => ({
      id: user.id,
      name: user.name || user.email || "Unknown User",
      keywords: [user.email, user.name, user.title].filter(Boolean) as string[],
      data: user
    }));
    // Offer a clear entry only when there is a selection to clear.
    if (clearable && !multiselect && value) {
      return [{ id: CLEAR_ITEM_ID, name: clearLabel }, ...userItems];
    }
    return userItems;
  }, [users, clearable, multiselect, value, clearLabel]);

  // Convert string value to SelectItem
  const selectedItem = useMemo<SelectItem<IUserSelectable> | null>(() => {
    if (!value) return null;
    return items.find((item) => item.id === value) || null;
  }, [items, value]);

  // Convert string array values to SelectItem array
  const selectedItems = useMemo<SelectItem<IUserSelectable>[]>(() => {
    if (!values || values.length === 0) return [];
    return items.filter((item) => values.includes(item.id));
  }, [items, values]);

  const handleSelect = (item: SelectItem<IUserSelectable> | null) => {
    // The clear entry unsets the current selection.
    if (item?.id === CLEAR_ITEM_ID) {
      onChangeValue?.("");
      return;
    }
    onChangeValue?.(item?.id ?? "");
  };

  const handleSelectMultiple = (items: SelectItem<IUserSelectable>[]) => {
    onChangeValues?.(items.map((item) => item.id));
  };

  const renderItem = (
    item: SelectItem<IUserSelectable>,
    _isSelected: boolean
  ): React.ReactNode => {
    if (!item.data) return item.name;
    return (
      <UserLabel
        user={item.data}
        variant="dropdown"
        showTitle
        showAvatar
        allowTruncate
      />
    );
  };

  const renderValue = (
    item: SelectItem<IUserSelectable> | SelectItem<IUserSelectable>[] | null
  ): React.ReactNode => {
    if (!item) return null;

    // Handle multiselect
    if (Array.isArray(item)) {
      if (item.length === 0) return null;
      if (item.length === 1 && item[0].data) {
        const user = item[0].data;
        return (
          <div className="flex min-w-0 items-center gap-2 overflow-hidden">
            {/* Decorative: the name follows, the initials would be read twice */}
            <span aria-hidden className="flex shrink-0">
              <UserAvatar user={user} className="h-5 w-5 shrink-0" />
            </span>
            <span className="truncate text-sm">
              {user.name || user.email || "Unknown User"}
            </span>
          </div>
        );
      }
      return <span>{item.length} users selected</span>;
    }

    // Handle single select - use compact inline display for the trigger
    if (!item.data) return item.name;
    const user = item.data;
    return (
      <div className="flex min-w-0 items-center gap-2 overflow-hidden">
        {/* h-5 keeps the avatar within the trigger's ~22px content box
            (UnifiedSelect's absolute inset-2.5 span) so it isn't clipped. */}
        <span aria-hidden className="flex shrink-0">
          <UserAvatar user={user} className="h-5 w-5 shrink-0" />
        </span>
        <span className="truncate text-sm">
          {user.name || user.email || "Unknown User"}
        </span>
      </div>
    );
  };

  return (
    <UnifiedSelect<IUserSelectable>
      {...rest}
      items={items}
      value={selectedItem}
      values={selectedItems}
      onSelect={handleSelect}
      onSelectMultiple={handleSelectMultiple}
      placeholder={isLoading ? "Loading users..." : placeholder}
      disabled={disabled || isLoading}
      multiselect={multiselect}
      searchable
      searchPlaceholder="Search users..."
      renderItem={renderItem}
      renderValue={renderValue}
      triggerClassName={cn(
        "flex h-[42px] w-full min-w-0 items-center justify-between gap-1 cursor-pointer text-sm rounded-md border border-input bg-background px-3 py-2 hover:bg-accent/50",
        (disabled || isLoading) &&
          "opacity-60 cursor-not-allowed !bg-secondary text-neutral-400 hover:bg-secondary",
        inputClassName
      )}
    />
  );
};

/**
 * A user field built on `UnifiedSelect`: picks one colleague (or several with `multiselect`)
 * from the `users` you pass, shown with avatar, name and title and searchable by name, email
 * and title. Values are user ids. When the choice is anyone in the organization use
 * `AsyncUserSelectInput`, which searches on the server. Inside react-hook-form use
 * `HookFormUserSelectInput` / `HookFormAsyncUserSelectInput`.
 */
export const UserSelectInput = React.forwardRef<
  React.ComponentRef<typeof FormControlWrapper>,
  UserSelectInputPropsWithFormControl
>((props, ref) => {
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(props);
  const { disableOutline, ...restInputProps } = inputProps;

  return (
    <FormControlWrapper
      variant="unstyled"
      wrapperClassName="py-0"
      enableSlotsInputFocus
      ref={ref}
      {...wrapperProps}
      isDisabled={wrapperProps.isDisabled || restInputProps.disabled}
    >
      {/* The trigger is not a labelable input, so it announces the label itself */}
      <UserSelectInputInner
        label={
          typeof wrapperProps.label === "string"
            ? wrapperProps.label
            : undefined
        }
        {...restInputProps}
      />
      {disableOutline !== true && <FormControlOutline />}
    </FormControlWrapper>
  );
});

UserSelectInput.displayName = "UserSelectInput";
